'use client';

import { type ComponentProps, createContext, useContext, useRef, useState } from 'react';
import {
    Root,
    Trigger,
    Close,
    Portal,
    Overlay,
    Content,
    Title,
    Description,
} from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { focusFirstField } from '@/util/focus';
import { cn } from '@/util/utils';

/** Whether the sheet's header draws the close button; set by SheetContent's showCloseButton. */
const SheetCloseContext = createContext(false);

interface SheetContentProps extends ComponentProps<typeof Content> {
    side?: SheetSide;
    /** The X in the corner. Off when the sheet is a navigation panel with its own way out. */
    showCloseButton?: boolean;
    /** The Add/Create product shape: tighter head and foot bands and no body padding. */
    flush?: boolean;
    /** 688px (the design's side panel) or its 700px wide one; full width on a phone either way. */
    size?: 'default' | 'wide';
}

enum SheetSide {
    TOP = 'top',
    RIGHT = 'right',
    BOTTOM = 'bottom',
    LEFT = 'left',
}

function Sheet({
    open,
    defaultOpen,
    modal,
    dirty = false,
    busy = false,
    onOpenChange,
    children,
    ...props
}: Omit<ComponentProps<typeof Root>, 'onOpenChange'> & {
    onOpenChange?: (open: boolean) => void;
    dirty?: boolean;
    busy?: boolean;
}) {
    const [discardOpen, setDiscardOpen] = useState(false);
    return (
        <Root
            data-slot="sheet"
            open={open}
            defaultOpen={defaultOpen}
            modal={modal}
            onOpenChange={(next) => {
                if (busy) return;
                if (!next && dirty) setDiscardOpen(true);
                else onOpenChange?.(next);
            }}
            {...props}
        >
            {children}
            <ConfirmDialog
                open={discardOpen}
                onOpenChange={setDiscardOpen}
                title="Discard unsaved changes?"
                description="Your changes have not been saved. Keep editing or discard them."
                confirmLabel="Discard changes"
                cancelLabel="Keep editing"
                destructive
                pending={busy}
                onConfirm={() => {
                    setDiscardOpen(false);
                    onOpenChange?.(false);
                }}
            />
        </Root>
    );
}

function SheetTrigger({ ref, asChild, ...props }: ComponentProps<typeof Trigger>) {
    return <Trigger data-slot="sheet-trigger" ref={ref} asChild={asChild} {...props} />;
}

function SheetClose({ ref, asChild, ...props }: ComponentProps<typeof Close>) {
    return <Close data-slot="sheet-close" ref={ref} asChild={asChild} {...props} />;
}

function SheetPortal({ container, forceMount, ...props }: ComponentProps<typeof Portal>) {
    return (
        <Portal data-slot="sheet-portal" container={container} forceMount={forceMount} {...props} />
    );
}

function SheetOverlay({
    forceMount,
    ref,
    asChild,
    className,
    ...props
}: ComponentProps<typeof Overlay>) {
    return (
        <Overlay
            data-slot="sheet-overlay"
            forceMount={forceMount}
            ref={ref}
            asChild={asChild}
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 duration-(--duration-base) motion-reduce:animate-none',
                className,
            )}
            {...props}
        />
    );
}

function SheetContent({
    flush,
    size = 'default',
    showCloseButton = true,
    className,
    children,
    side = SheetSide.RIGHT,
    forceMount,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    asChild,
    ref,
    ...props
}: SheetContentProps) {
    const returnFocus = useRef<HTMLElement | null>(null);
    return (
        <SheetPortal>
            <SheetOverlay />
            <Content
                data-slot="sheet-content"
                data-flush={flush || undefined}
                data-size={size}
                forceMount={forceMount}
                onOpenAutoFocus={(event) => {
                    returnFocus.current =
                        document.activeElement instanceof HTMLElement
                            ? document.activeElement
                            : null;
                    onOpenAutoFocus?.(event);
                    focusFirstField(event);
                }}
                onCloseAutoFocus={(event) => {
                    onCloseAutoFocus?.(event);
                    if (!event.defaultPrevented && returnFocus.current?.isConnected) {
                        event.preventDefault();
                        returnFocus.current.focus();
                    }
                }}
                onEscapeKeyDown={onEscapeKeyDown}
                onPointerDownOutside={onPointerDownOutside}
                onFocusOutside={onFocusOutside}
                onInteractOutside={onInteractOutside}
                asChild={asChild}
                ref={ref}
                className={cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
                    'group/sheet bg-card gap-0 shadow-lg ease-(--ease-standard) data-[state=closed]:duration-(--duration-base) data-[state=open]:duration-(--duration-base) motion-reduce:animate-none motion-reduce:transition-none',
                    side === SheetSide.RIGHT &&
                        'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 sm:max-w-sm',
                    side === SheetSide.LEFT &&
                        'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
                    side === SheetSide.TOP &&
                        'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
                    side === SheetSide.BOTTOM &&
                        'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
                    // The design's side panel is 688 wide, and 700 where a screen widens it (Add
                    // outfit, Adjust wallet, the filters: min(700px, 100vw)); either takes the
                    // whole width of a phone. After the side classes so it wins the merge.
                    (side === SheetSide.RIGHT || side === SheetSide.LEFT) &&
                        'w-full sm:max-w-(--layout-panel-width) data-[size=wide]:sm:max-w-[700px]',
                    className,
                )}
                {...props}
            >
                <SheetCloseContext.Provider value={showCloseButton}>
                    {children}
                </SheetCloseContext.Provider>
            </Content>
        </SheetPortal>
    );
}

/*
  Every drawer's head and footer are the muted band over a white body (ALS-252). The designs drew
  some white and some grey with no rule behind it; one style now, set here, and no drawer
  overrides it.
*/
function SheetHeader({ className, children, ref, ...props }: ComponentProps<'div'>) {
    const showClose = useContext(SheetCloseContext);
    return (
        <div
            data-slot="sheet-header"
            ref={ref}
            className={cn(
                'bg-muted border-border flex items-center gap-3 border-b p-6',
                'group-data-flush/sheet:gap-4 group-data-flush/sheet:border-b-0 group-data-flush/sheet:px-5 group-data-flush/sheet:pt-5 group-data-flush/sheet:pb-4',
                className,
            )}
            {...props}
        >
            {children}
            {/* In the head's own row, as the prototype's side-panel draws it: a 36px ghost
                icon button with a 15px X, after the titles, so nothing runs underneath it. */}
            {showClose ? (
                <Close asChild>
                    <Button
                        data-slot="sheet-close-button"
                        data-close-button
                        variant="ghost"
                        size="icon"
                        className="ml-auto"
                        aria-label="Close"
                    >
                        <XIcon className="size-[15px]" />
                    </Button>
                </Close>
            ) : null}
        </div>
    );
}

function SheetFooter({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-footer"
            ref={ref}
            className={cn(
                'bg-muted border-border flex shrink-0 flex-wrap items-center justify-end gap-2 border-t px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
                'group-data-flush/sheet:px-5 group-data-flush/sheet:py-[15px]',
                className,
            )}
            {...props}
        />
    );
}

function SheetTitle({ className, ref, asChild, ...props }: ComponentProps<typeof Title>) {
    return (
        <Title
            data-slot="sheet-title"
            ref={ref}
            asChild={asChild}
            className={cn(
                'text-section-title text-foreground group-data-flush/sheet:text-label-lg',
                className,
            )}
            {...props}
        />
    );
}

function SheetDescription({
    className,
    ref,
    asChild,
    ...props
}: ComponentProps<typeof Description>) {
    return (
        <Description
            data-slot="sheet-description"
            ref={ref}
            asChild={asChild}
            className={cn('text-muted-foreground text-xs', className)}
            {...props}
        />
    );
}

/** The icon tile at the head of a side panel: 32px, white so it reads on the muted head. Omitted on flush sheets. */
function SheetIcon({ className, ref, ...props }: ComponentProps<'span'>) {
    return (
        <span
            data-slot="sheet-icon"
            aria-hidden
            ref={ref}
            className={cn(
                'bg-card text-secondary-foreground inline-flex size-8 shrink-0 items-center justify-center rounded-md group-data-flush/sheet:hidden [&_svg]:size-[17px]',
                className,
            )}
            {...props}
        />
    );
}

/** Title and subtitle stacked, taking the rest of the header row. */
function SheetTitles({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-titles"
            ref={ref}
            className={cn('flex min-w-0 flex-1 flex-col gap-1', className)}
            {...props}
        />
    );
}

/** The scrolling middle. Padded 24, or flush to both edges on a flush sheet. */
function SheetBody({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-body"
            ref={ref}
            className={cn(
                'min-h-0 flex-1 overflow-y-auto p-6 group-data-flush/sheet:p-0',
                className,
            )}
            {...props}
        />
    );
}

/** .md-form__heading: the label style in muted, opened by a hairline unless it leads the form. */
function SheetSectionHeading({
    className,
    first = false,
    ref,
    ...props
}: ComponentProps<'h3'> & { first?: boolean }) {
    return (
        <h3
            data-slot="sheet-section-heading"
            ref={ref}
            className={cn(
                'text-label-md text-muted-foreground',
                !first && 'border-border mt-4 border-t pt-4',
                className,
            )}
            {...props}
        />
    );
}

/** Paired fields where space permits; one readable column on a narrow phone. */
function SheetFormRow({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-form-row"
            ref={ref}
            className={cn('grid grid-cols-1 items-start gap-2.5 sm:grid-cols-2', className)}
            {...props}
        />
    );
}

export {
    Sheet,
    SheetSectionHeading,
    SheetFormRow,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
    SheetIcon,
    SheetTitles,
    SheetBody,
    SheetSide,
};
